'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  ScheduleSlotsDay,
  groupSlotsByDate,
} from '@/app/(main)/(pages)/_components/schedules/_utils/group-slots-by-date';
import { getSchedulesSlotsByIds } from '@/app/(main)/(pages)/_components/schedules/_services/get-schedules-slot-by-id.service';
import { SearchApiDayTime } from '@/app/(main)/(pages)/_types/search-api-response.type';

export type { ScheduleSlotsDay };

export interface ScheduleSlotFilters {
  dayTime?: SearchApiDayTime;
  minDate?: string;
  maxDate?: string;
}

export interface UseScheduleSlotsParams {
  scheduleIds: string[];
  limitDays: number;
  filters: ScheduleSlotFilters;
}

export interface UseScheduleSlotsReturn {
  isLoading: boolean;
  days: ScheduleSlotsDay[];
  hasMore: boolean;
  offset: number;
  goNext(): void;
  goPrev(): void;
  refresh(): void;
}

export function useScheduleSlots({
  scheduleIds,
  limitDays,
  filters,
}: UseScheduleSlotsParams): UseScheduleSlotsReturn {
  const { dayTime, minDate, maxDate } = filters;
  const scheduleIdsKey = scheduleIds.join(',');

  const [days, setDays] = useState<ScheduleSlotsDay[]>([]);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(scheduleIds.length > 0);
  const [hasMore, setHasMore] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);
  const [prevScheduleIdsKey, setPrevScheduleIdsKey] = useState(scheduleIdsKey);
  const [prevLimitDays, setPrevLimitDays] = useState(limitDays);
  const [prevDayTime, setPrevDayTime] = useState(dayTime);
  const [prevMinDate, setPrevMinDate] = useState(minDate);
  const [prevMaxDate, setPrevMaxDate] = useState(maxDate);

  if (
    scheduleIdsKey !== prevScheduleIdsKey
    || limitDays !== prevLimitDays
    || dayTime !== prevDayTime
    || minDate !== prevMinDate
    || maxDate !== prevMaxDate
  ) {
    setPrevScheduleIdsKey(scheduleIdsKey);
    setPrevLimitDays(limitDays);
    setPrevDayTime(dayTime);
    setPrevMinDate(minDate);
    setPrevMaxDate(maxDate);
    setOffset(0);
    setDays([]);
    setHasMore(true);
    setIsLoading(scheduleIds.length > 0);
  }

  useEffect(() => {
    if (!scheduleIdsKey) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getSchedulesSlotsByIds({
      ids: scheduleIdsKey.split(','), limitDays, offsetDays: offset, dayTime, minDate, maxDate,
    })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setDays(groupSlotsByDate(result.slots));
        setHasMore(result.hasMore);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setDays([]);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scheduleIdsKey, limitDays, offset, dayTime, minDate, maxDate, refreshCount]);

  const goNext = useCallback(() => {
    if (!hasMore) {
      return;
    }
    setOffset((current) => current + limitDays);
  }, [hasMore, limitDays]);

  const goPrev = useCallback(() => {
    setOffset((current) => Math.max(0, current - limitDays));
  }, [limitDays]);

  const refresh = useCallback(() => {
    setRefreshCount((current) => current + 1);
  }, []);

  return { isLoading, days, hasMore, offset, goNext, goPrev, refresh };
}
